import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Paper, Chip, Button,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import DnsIcon from '@mui/icons-material/Dns';
import BrushIcon from '@mui/icons-material/Brush';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const C = {
  purple: '#552583', purpleDark: '#33154f', gold: '#FDB927',
  bg: '#E4F0F6', white: '#ffffff', text: '#1a1a1a', textSoft: '#4a4a4a',
};

const sectionContent = [
  {
    id: 'editeur',
    title: '1. Éditeur du site',
    icon: <BusinessIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Raison sociale',
        text: 'Usafiri\nPlateforme multi-services de Mayotte',
      },
      {
        subtitle: 'Siège social',
        text: 'Mayotte, France (976)',
      },
      {
        subtitle: 'Coordonnées',
        text: 'Email : contact@usafiri.yt\nSite web : https://usafiri.yt',
      },
      {
        subtitle: 'Directeur de la publication',
        text: 'Le représentant légal de la société Usafiri.',
      },
    ],
  },
  {
    id: 'hebergement',
    title: '2. Hébergement',
    icon: <DnsIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Hébergeur du site',
        text: 'Netlify, Inc.\n44 Montgomery Street, Suite 300\nSan Francisco, California 94104\nSite web : https://www.netlify.com',
      },
      {
        subtitle: 'Base de données',
        text: 'Supabase (Google Cloud Platform)\nRégion : Europe (eu-central-1)\nSite web : https://supabase.com',
      },
      {
        subtitle: 'Paiements',
        text: 'Stripe, Inc.\n354 Oyster Point Blvd, South San Francisco, CA 94080\nLes données bancaires sont traitées exclusivement par Stripe, certifié PCI-DSS niveau 1.',
      },
    ],
  },
  {
    id: 'conception',
    title: '3. Conception et réalisation',
    icon: <BrushIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Développement',
        text: 'La plateforme Usafiri a été conçue et développée en interne.\nTechnologies : React, Node.js, PostgreSQL, Supabase Edge Functions.',
      },
      {
        subtitle: 'Design',
        text: 'Interface conçue avec Material UI (MUI), adaptée pour une utilisation optimale sur tous les appareils (responsive design).',
      },
    ],
  },
  {
    id: 'propriete',
    title: '4. Propriété intellectuelle',
    icon: <CopyrightIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Droits d\'auteur',
        text: 'L\'ensemble du contenu de la plateforme Usafiri (textes, images, logos, icônes, sons, logiciels, base de données) est protégé par le droit de la propriété intellectuelle. Toute reproduction, représentation, modification ou exploitation non autorisée est interdite.',
      },
      {
        subtitle: 'Marques',
        text: 'Le nom "Usafiri", le logo et les éléments graphiques associés sont des marques déposées ou en cours de dépôt. Toute utilisation non autorisée est passible de poursuites.',
      },
      {
        subtitle: 'Contenu utilisateur',
        text: 'Les contenus publiés par les utilisateurs (avis, photos, descriptions) restent leur propriété. En les publiant sur la plateforme, ils accordent à Usafiri une licence non exclusive d\'utilisation pour les besoins du service.',
      },
    ],
  },
  {
    id: 'contact',
    title: '5. Contact',
    icon: <ContactMailIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Pour toute question',
        text: 'Email : contact@usafiri.yt\n\nConformément à la loi n° 2004-575 du 21 juin 2004 pour la confiance dans l\'économie numérique (LCEN), les présentes mentions légales sont accessibles à tout moment depuis le pied de page du site.',
      },
    ],
  },
];

export default function MentionsLegales() {
  const navigate = useNavigate();

  return (
    <Box sx={{ background: C.bg, minHeight: '100vh' }}>
      {/* Hero */}
      <Box sx={{
        background: `linear-gradient(135deg, ${C.purpleDark} 0%, ${C.purple} 60%, #7a3aad 100%)`,
        color: C.white, py: { xs: 6, md: 10 }, px: 2, textAlign: 'center',
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: 'rgba(253,185,39,0.08)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box sx={{
            width: 72, height: 72, borderRadius: '20px',
            background: 'rgba(253,185,39,0.15)', border: '1.5px solid rgba(253,185,39,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <BusinessIcon sx={{ fontSize: 38, color: C.gold }} />
          </Box>
        </Box>

        <Typography variant="h3" fontWeight={800} sx={{ mb: 1.5, fontSize: { xs: '1.8rem', md: '2.6rem' }, letterSpacing: '-0.5px' }}>
          Mentions Légales
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.75, mb: 3, maxWidth: 500, mx: 'auto' }}>
          Informations légales obligatoires conformément à la loi LCEN
        </Typography>

        <Chip label="Conforme LCEN" sx={{ bgcolor: 'rgba(253,185,39,0.18)', color: C.gold, fontWeight: 700, fontSize: '0.75rem', border: '1px solid rgba(253,185,39,0.3)' }} />
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {sectionContent.map((section) => (
            <Paper key={section.id} elevation={0} sx={{
              borderRadius: '20px', background: C.white,
              border: '1px solid rgba(85,37,131,0.08)', boxShadow: '0 4px 24px rgba(85,37,131,0.06)',
              overflow: 'hidden',
            }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                p: { xs: 2.5, md: 3.5 },
                background: 'linear-gradient(135deg, rgba(85,37,131,0.04) 0%, rgba(253,185,39,0.04) 100%)',
                borderBottom: '1px solid rgba(85,37,131,0.08)',
              }}>
                <Box sx={{
                  width: 48, height: 48, borderRadius: '14px',
                  background: 'rgba(85,37,131,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {section.icon}
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: C.text }}>
                  {section.title}
                </Typography>
              </Box>

              <Box sx={{ p: { xs: 2.5, md: 3.5 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {section.content.map((item, i) => (
                  <Box key={i}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: C.purple, mb: 0.5, fontSize: '0.85rem' }}>
                      {item.subtitle}
                    </Typography>
                    <Typography variant="body2" sx={{ color: C.textSoft, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          ))}
        </Box>

        <Box sx={{ mt: 5, textAlign: 'center' }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
            sx={{ borderColor: C.purple, color: C.purple, borderRadius: '12px', textTransform: 'none', fontWeight: 600,
              '&:hover': { borderColor: C.purpleDark, bgcolor: 'rgba(85,37,131,0.04)' },
            }}>
            Retour
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
